import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { getParents, inviteParent, resendInvite, deleteParent, getParentChildren } from "../../api/parentApi";

const emptyForm = { name: "", email: "", phone: "" };

export default function ParentsPage() {
  const toast = useRef(null);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [childrenDialog, setChildrenDialog] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getParents();
      setParents(data);
    } catch {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load parents" });
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setForm(emptyForm);
    setDialogVisible(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.current.show({ severity: "warn", summary: "Validation", detail: "Name and email are required" });
      return;
    }
    setSaving(true);
    try {
      await inviteParent(form);
      toast.current.show({ severity: "success", summary: "Invited", detail: "Invitation sent to " + form.email });
      setDialogVisible(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to send invite";
      toast.current.show({ severity: "error", summary: "Error", detail: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (parent) => {
    try {
      await resendInvite(parent.id);
      toast.current.show({ severity: "success", summary: "Sent", detail: "Invite resent to " + parent.email });
      load();
    } catch {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to resend invite" });
    }
  };

  const handleDelete = (parent) => {
    confirmDialog({
      message: `Delete parent account for ${parent.name}? This will not delete their linked students.`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteParent(parent.id);
          toast.current.show({ severity: "success", summary: "Deleted", detail: "Parent removed" });
          load();
        } catch {
          toast.current.show({ severity: "error", summary: "Error", detail: "Delete failed" });
        }
      },
    });
  };

  // ── Column templates ──────────────────────────────────────────────────────

  const statusBody = (row) => {
    if (row.isActive) return <Tag severity="success" value="Active" />;
    if (row.invitePending) return <Tag severity="warning" value="Invite Pending" />;
    return <Tag severity="danger" value="Inactive" />;
  };

  const openChildrenDialog = async (parent) => {
    setSelectedParent(parent);
    setChildren([]);
    setChildrenDialog(true);
    setChildrenLoading(true);
    try {
      const data = await getParentChildren(parent.id);
      setChildren(data.items || []);
    } catch {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load children" });
    } finally {
      setChildrenLoading(false);
    }
  };

  const childrenBody = (row) => (
    <Button
      label={String(row.childrenCount)}
      size="small"
      text
      className="p-0 font-semibold"
      disabled={row.childrenCount === 0}
      onClick={() => openChildrenDialog(row)}
      tooltip={row.childrenCount > 0 ? "View children" : undefined}
      tooltipOptions={{ position: "top" }}
    />
  );

  const actionsBody = (row) => (
    <div className="flex gap-2">
      {!row.isActive && (
        <Button
          icon="pi pi-envelope"
          size="small"
          text
          tooltip={row.invitePending ? "Resend invite" : "Send invite"}
          tooltipOptions={{ position: "top" }}
          onClick={() => handleResend(row)}
        />
      )}
      <Button
        icon="pi pi-trash"
        size="small"
        text
        severity="danger"
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleDelete(row)}
      />
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="m-0 text-xl font-bold">Parents</h2>
          <p className="m-0 text-color-secondary text-sm mt-1">
            Manage parent accounts and send invitations
          </p>
        </div>
        <Button label="Invite Parent" icon="pi pi-user-plus" onClick={openNew} />
      </div>

      <DataTable
        value={parents}
        loading={loading}
        paginator
        rows={15}
        rowsPerPageOptions={[10, 15, 25, 50]}
        emptyMessage="No parents found"
        stripedRows
      >
        <Column field="name" header="Name" sortable />
        <Column field="email" header="Email" sortable />
        <Column field="phone" header="Phone" />
        <Column header="Children" body={childrenBody} style={{ width: "6rem", textAlign: "center" }} />
        <Column header="Status" body={statusBody} style={{ width: "9rem" }} />
        <Column header="Actions" body={actionsBody} style={{ width: "8rem" }} />
      </DataTable>

      {/* Children Dialog */}
      <Dialog
        header={selectedParent ? `Children — ${selectedParent.name}` : "Children"}
        visible={childrenDialog}
        style={{ width: "560px" }}
        onHide={() => setChildrenDialog(false)}
      >
        {childrenLoading ? (
          <div className="flex justify-content-center p-4">
            <i className="pi pi-spin pi-spinner text-2xl" />
          </div>
        ) : children.length === 0 ? (
          <p className="text-color-secondary text-center p-3">No children linked to this parent.</p>
        ) : (
          <DataTable value={children} size="small" stripedRows>
            <Column field="full_name" header="Name" />
            <Column header="Grade" body={(r) => r.currentGrade?.name || "—"} />
            <Column header="Enrollment" body={(r) => r.enrollmentStatus
              ? <Tag value={r.enrollmentStatus} severity={r.enrollmentStatus === "active" ? "success" : "warning"} />
              : <span className="text-color-secondary">—</span>
            } />
            <Column header="Relation" body={(r) => (
              <Tag value={r.relation} severity={r.relation === "Primary Guardian" ? "info" : "secondary"} />
            )} />
          </DataTable>
        )}
      </Dialog>

      {/* Invite Dialog */}
      <Dialog
        header="Invite Parent"
        visible={dialogVisible}
        style={{ width: "420px" }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" text onClick={() => setDialogVisible(false)} />
            <Button label="Send Invite" icon="pi pi-envelope" loading={saving} onClick={handleSave} />
          </div>
        }
      >
        <div className="flex flex-column gap-3 mt-2">
          <div className="flex flex-column gap-1">
            <label className="font-medium text-sm">Full Name *</label>
            <InputText
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Parent's full name"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="font-medium text-sm">Email *</label>
            <InputText
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="parent@example.com"
              type="email"
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="font-medium text-sm">Phone</label>
            <InputText
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Optional"
            />
          </div>
          <p className="m-0 text-sm text-color-secondary">
            An invitation email will be sent to the parent to set their password and activate their account.
          </p>
        </div>
      </Dialog>
    </div>
  );
}
